import React, { useState, useEffect } from 'react';
import { Search, Plus } from 'lucide-react';
import StitchDetailModal from './StitchDetailModal';
import LogoMark from './LogoMark';

function StitchLibrary({ colors, fonts, onBack, initialStitchId, stitches, isAdmin, onAddStitch, onEditStitch, onDeleteStitch, patterns, onPatternClick }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedStitch, setSelectedStitch] = useState(null);

  // Auto-open stitch from URL
  useEffect(() => {
    if (initialStitchId) {
      const stitch = stitches.find(s => s.id === initialStitchId);
      if (stitch) {
        setSelectedStitch(stitch);
      }
    }
  }, [initialStitchId, stitches]);

  const categories = ["all", "Foundational", "Textured", "Decorative", "Special"];

  const filteredStitches = stitches.filter(stitch => {
    const matchesSearch = !searchQuery ||
      stitch.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      stitch.abbreviation.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "all" || stitch.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ marginBottom: "2rem" }}>
        {/* Back button */}
        {onBack && (
          <button
            onClick={onBack}
            style={{
              background: "none",
              border: "none",
              color: colors.sageGreen,
              fontSize: "1rem",
              fontWeight: "600",
              cursor: "pointer",
              marginBottom: "1rem",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem"
            }}
          >
            ← Back to Resources
          </button>
        )}

        {/* Title and Add Stitch Button */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h1 style={{
              fontSize: "2.5rem",
              color: colors.charcoal,
              marginBottom: "0.5rem",
              fontFamily: fonts.display
            }}>
              Stitch Library
            </h1>
            <p style={{ color: colors.darkGray, fontSize: "1.1rem" }}>
              {filteredStitches.length} {filteredStitches.length === 1 ? 'stitch' : 'stitches'}
            </p>
          </div>

          {isAdmin && (
            <button
              onClick={onAddStitch}
              style={{
                padding: "0.75rem 1.5rem",
                background: `linear-gradient(135deg, ${colors.sageGreen} 0%, #8e9775 100%)`,
                color: "#fff",
                border: "none",
                borderRadius: "50px",
                fontSize: "0.95rem",
                fontWeight: "600",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                boxShadow: "0 4px 12px rgba(163, 177, 138, 0.3)"
              }}
            >
              <Plus size={18} />
              Add Stitch
            </button>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div style={{
        background: "#fff",
        padding: "1.5rem",
        borderRadius: "16px",
        marginBottom: "2rem",
        boxShadow: "0 4px 12px rgba(0,0,0,0.08)"
      }}>
        {/* Search */}
        <div style={{ position: "relative", marginBottom: "1rem" }}>
          <Search
            size={20}
            style={{
              position: "absolute",
              left: "1rem",
              top: "50%",
              transform: "translateY(-50%)",
              color: colors.brownGray
            }}
          />
          <input
            type="text"
            placeholder="Search stitches..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: "100%",
              padding: "1rem 1rem 1rem 3rem",
              border: `2px solid ${colors.borderGreige}`,
              borderRadius: "12px",
              fontSize: "1rem",
              outline: "none"
            }}
          />
        </div>

        {/* Category Filters */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem" }}>
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              style={{
                padding: "0.5rem 1rem",
                background: selectedCategory === cat ? colors.sageGreen : "#fff",
                color: selectedCategory === cat ? "#fff" : colors.brownGray,
                border: `2px solid ${selectedCategory === cat ? colors.sageGreen : colors.borderGreige}`,
                borderRadius: "50px",
                fontSize: "0.9rem",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.2s"
              }}
            >
              {cat === "all" ? "All Stitches" : cat}
            </button>
          ))}
        </div>
      </div>

      {/* Stitch Grid */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
        gap: "1.5rem"
      }}>
        {filteredStitches.map(stitch => (
          <div
            key={stitch.id}
            onClick={() => setSelectedStitch(stitch)}
            style={{
              background: "#fff",
              borderRadius: "16px",
              overflow: "hidden",
              cursor: "pointer",
              transition: "all 0.2s",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              position: "relative"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-4px)";
              e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
            }}
          >
            {/* Admin Buttons */}
            {isAdmin && (
              <>
                {/* Edit Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditStitch(stitch);
                  }}
                  style={{
                    position: "absolute",
                    top: "0.5rem",
                    right: "2.5rem",
                    background: "rgba(255,255,255,0.95)",
                    border: "none",
                    width: "28px",
                    height: "28px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    fontSize: "0.9rem",
                    color: colors.sageGreen,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    zIndex: 10,
                    fontWeight: "bold"
                  }}
                  title="Edit stitch"
                >
                  ✏️
                </button>

                {/* Delete Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteStitch(stitch.id);
                  }}
                  style={{
                    position: "absolute",
                    top: "0.5rem",
                    right: "0.5rem",
                    background: "rgba(255,255,255,0.95)",
                    border: "none",
                    width: "28px",
                    height: "28px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    fontSize: "1.1rem",
                    color: colors.dustyRust,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    zIndex: 10,
                    fontWeight: "bold"
                  }}
                  title="Delete stitch"
                >
                  ×
                </button>
              </>
            )}

            {/* Image */}
            <div style={{
              width: "100%",
              height: "200px",
              overflow: "hidden",
              position: "relative"
            }}>
              <img
                src={stitch.image}
                alt={stitch.name}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover"
                }}
                onError={(e) => {
                  // Fallback to emoji if image fails to load
                  e.target.style.display = "none";
                  e.target.parentElement.innerHTML = '<div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background: linear-gradient(135deg, #a3b18a20, #d4a57420)"><svg xmlns="http://www.w3.org/2000/svg" width="160" height="64" viewBox="0 0 500 200" fill="none"><path d="M50 140 C 120 140, 160 140, 190 110 C 220 80, 210 40, 185 45 C 165 50, 155 80, 155 80 C 155 80, 145 50, 125 45 C 100 40, 90 80, 120 110 C 150 140, 190 140, 260 140 L 450 140" stroke="#8A9A5B" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"/></svg></div>';
                }}
              />
            </div>

            {/* Content */}
            <div style={{ padding: "1.25rem" }}>
              <div style={{
                fontSize: "1.25rem",
                fontWeight: "700",
                color: colors.charcoal,
                marginBottom: "0.25rem"
              }}>
                {stitch.name}
              </div>
              <div style={{
                fontSize: "0.95rem",
                color: colors.brownGray,
                marginBottom: "0.75rem"
              }}>
                {stitch.abbreviation}
              </div>
              <div style={{
                display: "inline-block",
                padding: "0.25rem 0.75rem",
                background: stitch.difficulty === "Beginner Friendly" ? "#9ec5d4" :
                           stitch.difficulty === "Beginner" ? colors.sageGreen :
                           stitch.difficulty === "Intermediate" ? colors.mustard : colors.dustyRust,
                color: "#fff",
                borderRadius: "50px",
                fontSize: "0.75rem",
                fontWeight: "600"
              }}>
                {stitch.difficulty}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* No Results */}
      {filteredStitches.length === 0 && (
        <div style={{
          background: "#fff",
          borderRadius: "16px",
          padding: "3rem",
          textAlign: "center",
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)"
        }}>
          <p style={{ fontSize: "1.2rem", color: colors.brownGray }}>
            No stitches found
          </p>
          <button
            onClick={() => {
              setSearchQuery("");
              setSelectedCategory("all");
            }}
            style={{
              marginTop: "1rem",
              padding: "0.75rem 1.5rem",
              background: colors.sageGreen,
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              fontSize: "1rem",
              fontWeight: "600",
              cursor: "pointer"
            }}
          >
            Clear Filters
          </button>
        </div>
      )}

      {/* Stitch Detail Modal */}
      {selectedStitch && (
        <StitchDetailModal
          stitch={selectedStitch}
          onClose={() => setSelectedStitch(null)}
          colors={colors}
          fonts={fonts}
          patterns={patterns}
          onPatternClick={onPatternClick}
        />
      )}
    </div>
  );
}

export default StitchLibrary;
